import { makeAutoObservable, computed, runInAction } from 'mobx';
import { format } from 'date-fns';
import type { IDeviceListItem, DeviceStatusValue } from '@emfit/shared-types';
import { DeviceStatus } from '@emfit/shared-types';
import { Wire } from 'src/services/Wire';
import { timeSinceDate } from 'src/utils/timeSince';
import { EmfitAPI } from 'src/services/BaseAPI';
import { STATUS_API } from 'src/config';
import type { AppDeviceSettings } from './DevicesStore';
import type { NotificationSettings } from '../components/NotificationIconStrip';

/**
 * DeviceStore - Wraps AppDeviceSettings and reads real-time status from Wire
 *
 * Follows qs2 pattern:
 * - Wraps device settings object (like qs2 wraps Device)
 * - Computed properties read from Wire.deviceStatus[deviceId]
 * - Implements IDeviceListItem for shared UI components
 * - Observable setters for settings management with auto-change detection
 */
export class DeviceStore implements IDeviceListItem {
  device: AppDeviceSettings;
  private originalDevice: string; // JSON string for comparison

  constructor(device: AppDeviceSettings) {
    this.device = device;
    this.originalDevice = JSON.stringify(device);
    makeAutoObservable(
      this,
      {
        id: computed,
        deviceId: computed,
        deviceName: computed,
        serialNumber: computed,
        status: computed,
        lastSeen: computed,
        lastSeenAbsolute: computed,
        lastSeenTimestamp: computed,
        withoutTurningSince: computed,
        field1: computed,
        field2: computed,
        notificationSettings: computed,
        isDirty: computed,
      },
      { autoBind: true }
    );
  }

  // IDeviceListItem implementation

  get id(): string {
    return this.device.device_id;
  }

  get deviceId(): string {
    return this.device.device_id;
  }

  get deviceName(): string {
    return this.device.device_name || '';
  }

  get serialNumber(): string {
    return this.device.serial_number || '';
  }

  get status(): DeviceStatusValue {
    const deviceStatus = Wire.deviceStatus[this.deviceId];
    return deviceStatus?.status ?? DeviceStatus.Undeployed;
  }

  get lastSeen(): string {
    const deviceStatus = Wire.deviceStatus[this.deviceId];
    if (!deviceStatus?.statussince) {
      return 'Never';
    }
    return timeSinceDate(new Date(deviceStatus.statussince).getTime());
  }

  get lastSeenAbsolute(): string {
    const deviceStatus = Wire.deviceStatus[this.deviceId];
    if (!deviceStatus?.statussince) {
      return 'Never';
    }
    return format(new Date(deviceStatus.statussince), 'MMM d, yyyy, h:mm a');
  }

  get lastSeenTimestamp(): number {
    const deviceStatus = Wire.deviceStatus[this.deviceId];
    if (!deviceStatus?.statussince) {
      return 0;
    }
    return new Date(deviceStatus.statussince).getTime();
  }

  /**
   * Start of the current stretch in bed without turning: getting into bed or
   * the last detected turn, whichever is later. 0 when out of bed, or when
   * qs-status sends no turn time (turning notifications off).
   */
  get withoutTurningSince(): number {
    if (this.status !== DeviceStatus.Presence) return 0;
    const lastTurn = Wire.deviceStatus[this.deviceId]?.lastTurnEventAt;
    if (!lastTurn) return 0;
    const turnedAt = new Date(lastTurn).getTime();
    if (Number.isNaN(turnedAt)) return 0;
    return Math.max(turnedAt, this.lastSeenTimestamp);
  }

  get field1(): string | null {
    return this.device.field1 || null;
  }

  get field2(): string | null {
    return this.device.field2 || null;
  }

  get notificationSettings(): NotificationSettings {
    const d = this.device;
    return {
      notifications: d.notifications ?? false,
      nightTime: d.night_time ?? false,
      dayTime: d.day_time ?? false,
      presenceNotice: d.presence_notice ?? false,
      absenceNotice: d.absence_notice ?? false,
      noMovement: d.no_movement ?? false,
      intentionToLeave: d.intention_to_leave ?? false,
      restless: d.restless ?? false,
      turning: d.turning ?? false,
      fastMovement: d.fast_movement ?? false,
      sitting: d.sitting ?? false,
    };
  }

  // Time value getters - return Date objects for locale-aware formatting

  /**
   * Get night_start as Date object (locale-independent)
   * View components should use date-fns to format with user's locale
   */
  get nightStartTime(): Date | null {
    if (!this.device.night_start || this.device.night_start.length !== 4) {
      return null;
    }
    const hours = parseInt(this.device.night_start.slice(0, 2), 10);
    const minutes = parseInt(this.device.night_start.slice(2), 10);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  /**
   * Get night_end as Date object (locale-independent)
   * View components should use date-fns to format with user's locale
   */
  get nightEndTime(): Date | null {
    if (!this.device.night_end || this.device.night_end.length !== 4) {
      return null;
    }
    const hours = parseInt(this.device.night_end.slice(0, 2), 10);
    const minutes = parseInt(this.device.night_end.slice(2), 10);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  // Additional getters for device-specific properties (optional, may not exist on all device types)

  get email(): string {
    return (this.device as any).email || '';
  }

  get timezone(): string {
    return (this.device as any).timezone_id?.toString() || '';
  }

  get locale(): string {
    return (this.device as any).locale || '';
  }

  // Real-time device status from Wire

  get connected(): boolean {
    const deviceStatus = Wire.deviceStatus[this.deviceId];
    return deviceStatus?.connected ?? false;
  }

  get firmwareVersion(): string {
    const deviceStatus = Wire.deviceStatus[this.deviceId];
    return deviceStatus?.firmwareversion ?? '';
  }

  get lastMotionDetected(): Date | null {
    const deviceStatus = Wire.deviceStatus[this.deviceId];
    return deviceStatus?.lastmotiondetected ?? null;
  }

  get motionSensorStatus(): number | null {
    const deviceStatus = Wire.deviceStatus[this.deviceId];
    return deviceStatus?.motionsensorstatus ?? null;
  }

  // Settings management

  /**
   * Check if device has unsaved changes
   */
  get isDirty(): boolean {
    return JSON.stringify(this.device) !== this.originalDevice;
  }

  /**
   * Reset to original state
   */
  reset(): void {
    this.device = JSON.parse(this.originalDevice);
  }

  /**
   * Mark current state as saved (update baseline)
   */
  markAsSaved(): void {
    this.originalDevice = JSON.stringify(this.device);
  }

  /**
   * Get device settings for /v1/device/settings API
   * Only includes notification and alert settings
   */
  getDeviceSettings() {
    return {
      device_id: this.device.device_id,
      parent_device_id: this.device.parent_device_id || null,
      location: this.device.location || null,
      notifications: this.device.notifications,
      absence_notice: this.device.absence_notice ?? false,
      presence_notice: this.device.presence_notice ?? false,
      night_start: this.device.night_start || '',
      night_end: this.device.night_end || '',
      night_time: this.device.night_time ?? false,
      night_delay: this.device.night_delay ?? 0,
      day_time: this.device.day_time ?? false,
      day_delay: this.device.day_delay ?? 0,
      turning: this.device.turning ?? false,
      turning_delay: this.device.turning_delay ?? 0,
      sitting: this.device.sitting ?? false,
      sitting_period: this.device.sitting_period ?? 1,
      restless: this.device.restless ?? false,
      fast_movement: this.device.fast_movement ?? false,
      intention_to_leave: this.device.intention_to_leave ?? false,
      no_movement: this.device.no_movement ?? false,
      no_movement_delay: this.device.no_movement_delay ?? 0,
      send_delayed_bed_notifications:
        this.device.send_delayed_bed_notifications ?? false,
      evening_days: this.device.evening_days ?? 127,
      morning_days: this.device.morning_days ?? 127,
      only_movement_detection: this.device.only_movement_detection ?? false,
      allow_notification_muting: this.device.allow_notification_muting ?? false,
      movement_alert_cooldown_mins: this.device.movement_alert_cooldown_mins ?? 5,
      motion_silenced: this.device.motion_silenced ?? false,
      motion_silence_mode: this.device.motion_silence_mode ?? 1,
      turning_delay_day: this.device.turning_delay_day ?? 0,
    };
  }

  /**
   * Get device info for /v1/device/{id} API
   * Only includes name and custom fields
   */
  getDeviceInfo() {
    return {
      deviceName: this.device.device_name,
      field1: this.device.field1 || '',
      field2: this.device.field2 || '',
    };
  }

  /**
   * Save device settings to API
   * POST /v1/device with settings payload
   */
  async saveSettings(): Promise<boolean> {
    try {
      await EmfitAPI.put(`${STATUS_API}/v1/device`, {
        body: JSON.stringify([this.getDeviceSettings()]),
      });
      runInAction(() => {
        this.markAsSaved();
      });
      return true;
    } catch (error) {
      console.error('Failed to save device settings:', error);
      return false;
    }
  }

  // Observable setters for editable fields

  setDeviceName(value: string): void {
    this.device.device_name = value;
  }

  setField1(value: string): void {
    this.device.field1 = value;
  }

  setField2(value: string): void {
    this.device.field2 = value;
  }

  setNotifications(value: boolean): void {
    this.device.notifications = value;
  }

  setPresenceNotice(value: boolean): void {
    this.device.presence_notice = value;
  }

  setAbsenceNotice(value: boolean): void {
    this.device.absence_notice = value;
  }

  setNightTime(value: boolean): void {
    this.device.night_time = value;
  }

  /**
   * Set night_start in HHMM format (backend format)
   * @param value - Time in HHMM format (e.g., "2100")
   */
  setNightStart(value: string): void {
    this.device.night_start = value;
  }

  /**
   * Set night_end in HHMM format (backend format)
   * @param value - Time in HHMM format (e.g., "0800")
   */
  setNightEnd(value: string): void {
    this.device.night_end = value;
  }

  setNightDelay(value: number): void {
    this.device.night_delay = value;
  }

  setDayTime(value: boolean): void {
    this.device.day_time = value;
  }

  setDayDelay(value: number): void {
    this.device.day_delay = value;
  }

  setTurning(value: boolean): void {
    this.device.turning = value;
  }

  setTurningDelay(value: number): void {
    this.device.turning_delay = value;
  }

  setTurningDelayDay(value: number): void {
    this.device.turning_delay_day = value;
  }

  setSitting(value: boolean): void {
    this.device.sitting = value;
  }

  setSittingPeriod(value: number): void {
    this.device.sitting_period = value;
  }

  setRestless(value: boolean): void {
    this.device.restless = value;
  }

  setFastMovement(value: boolean): void {
    this.device.fast_movement = value;
  }

  setIntentionToLeave(value: boolean): void {
    this.device.intention_to_leave = value;
  }

  setNoMovement(value: boolean): void {
    this.device.no_movement = value;
  }

  setNoMovementDelay(value: number): void {
    this.device.no_movement_delay = value;
  }

  setSendDelayedBedNotifications(value: boolean): void {
    this.device.send_delayed_bed_notifications = value;
  }

  setEveningDays(value: number): void {
    this.device.evening_days = value;
  }

  setMorningDays(value: number): void {
    this.device.morning_days = value;
  }

  setOnlyMovementDetection(value: boolean): void {
    this.device.only_movement_detection = value;
    // When enabled, disable other PIR features
    if (value) {
      this.device.no_movement = false;
      this.device.sitting = false;
    }
  }

  setMotionSilenced(value: boolean): void {
    this.device.motion_silenced = value;
  }

  setMotionSilenceMode(value: number): void {
    this.device.motion_silence_mode = value;
  }
}
